import * as THREE from 'three';
import { safeZoneUniforms } from './shaders/safeZone.glsl.js';

// PosterBackdrop - le fond de Poster, extrait tel quel de visualizers/poster.js
// pour que Pyro puisse le prendre comme habillage. Rien ici n'est nouveau :
// le shader du fond, ses plafonds, sa couleur (une teinte, qui change sur la
// mesure, une seconde autorisée une phrase sur quatre) et le bandeau
// typographique sont le texte de Poster, et Poster rend à l'identique à
// travers ce module (vérifié en faisant tourner l'ancienne et la nouvelle
// classe sur le même déroulé et en comparant chaque uniforme à chaque image).
//
// LES QUATRE COUCHES DU NOIR, de la plus faible à la plus forte :
//   grille de points   3%   un mur de LED éteint vu de près - le pas de 24 px
//                           donne une SURFACE au noir sans le remplir
//   bandeau typo      ~5%   le titre en très grand, en tube néon éteint
//   mur LED          0-22%  éteint, traversé d'une vague sur la mesure
//   lueurs radiales   15%   elles marquent la place des pictogrammes, même
//                           quand il n'y a pas de pictogramme
//
// Ce que le module NE porte PAS : les pictogrammes, qui restent à Poster. Il
// écrit pourtant uTubeA/uTubeB, la teinte courante des tubes, parce que la
// couleur est UN état - les tubes, les lueurs, le mur et le bandeau la
// partagent - et qu'il n'y a qu'un endroit pour la tenir.
//
// Une option en plus pour Pyro : `glows`, six sources chaudes (les gerbes qui
// brûlent) ajoutées au fond avant la zone de sécurité, repliées par Reinhard
// comme dans l'ancien fond de Pyro. Poster ne la passe pas et son texte de
// shader reste byte pour byte le sien.

// --- les plafonds de luminance ---------------------------------------------
// Le seuil de bloom de la chaîne partagée vaut 0.78. Ce qui NE DOIT PAS
// bloomer est plafonné au CANAL MAXIMAL, qui majore la luminance ; le mur
// ALLUMÉ (drop) doit déborder et est plafonné en LUMINANCE, la grandeur
// seuillée. Voir poster.js pour la mesure qui a fixé cette règle.
export const CRETE_MUR = 0.88;     // LUMINANCE. Le mur ALLUMÉ (drop) déborde aussi.
export const CRETE_LUEUR = 0.40;   // canal maximal : majore la luminance, donc sûr
export const CRETE_TYPO = 0.38;    // canal maximal, pour la même raison
export const LUMA = 'vec3(0.2125, 0.7154, 0.0721)';

// La zone de sécurité, remappée : le plancher partagé (0.32) est relevé à
// 0.50 ici - un tube néon derrière la pochette éteint à 0.32 lisait comme
// une panne.
export const PLANCHER_PARTAGE = 0.32;
export const PLANCHER = 0.50;

// La géométrie des deux emplacements, en unités de hauteur (le cadre fait 2
// de haut, 2 * aspect de large).
const EMPLACEMENT_LARGEUR = 0.44;   // ~22% de la largeur du cadre, en demi-unités
const MARGE_POCHETTE = 0.10;
// Le rayon qu'un emplacement peut couvrir au tirage le plus large : +20% de
// taille, punch à 145%, troisième anneau d'écho à 1.6x.
const RAYON_MAX = EMPLACEMENT_LARGEUR * 0.5 * 1.20 * 1.45 * (1 + 3 * 0.20);

// --- l'habillage du noir ---------------------------------------------------
// LES NIVEAUX CI-DESSOUS SONT CALIBRÉS SUR LA SORTIE, pas posés à la main. Les
// « N% d'intensité » du brief sont des pourcentages de ce qu'on VOIT ; ces
// constantes multiplient de la lumière LINÉAIRE, et entre les deux il y a ACES
// à l'exposition 1.1 puis la courbe de sortie, dont la pente près de zéro vaut
// presque 13. Un 0.03 linéaire ressort à 24 sur 255, soit 9%, pas 3%. Chaque
// niveau a donc été mesuré couche par couche (les autres éteintes par uniforme)
// et réglé sur sa cible en octets : points 3% -> 8, lueur 15% -> 38, tube du
// bandeau éteint -> 20, vague -> 70.
const DITHER = 0.030;            // +/-1.5%, MULTIPLICATIF (voir le fragment du fond)
const POINTS_PAS_PX = 24.0;
const POINTS_NIVEAU = 0.010;
const MUR_PAS_PX = 44.0;         // les cases du mur LED, plus grosses que les points
const MUR_COUTURE = 16.0;        // une couture tous les 16 pavés, comme sur du vrai
const MUR_VAGUE_NIVEAU = 0.048;  // « à faible intensité »
const MUR_PLEIN_NIVEAU = 1.00;   // le drop : tout allumé
// Le gain qui emmène le mur PLEIN au-dessus du seuil de bloom, et le
// blanchiment sans lequel aucune multiplication n'y suffirait (voir le plafond).
const MUR_GAIN = 1.60;
const MUR_BLANCHIT = 0.55;
const LUEUR_RAYON = 0.30;        // 30% de la LARGEUR du cadre
const LUEUR_NIVEAU = 0.048;
const LUEUR_PULSE = 0.15;
const LUEUR_SUIVI_TEMPS = 1.0;   // la lueur suit la teinte en un temps
const TYPO_VITESSE = 0.026;      // unités de hauteur par seconde
const TYPO_CARS = 12;            // un caractère sur douze s'allume
const TYPO_ETEINT_TUBE = 0.023;  // « presque invisible »
const TYPO_ETEINT_HALO = 0.007;
// Une phrase sur quatre s'autorise une seconde teinte. Jamais trois.
const PHRASE_BICOLORE = 4;

// Ce que chaque tier allume dans le fond. Poster y ajoute `echo` pour ses
// pictogrammes ; Pyro prend la table telle quelle.
export const BACKDROP_TIERS = {
  2: { points: true, vague: true, mur: true },
  1: { points: false, vague: false, mur: true },
  0: { points: false, vague: false, mur: false }
};

// Le même remappage que le chunk partagé, mais évalué dans le FRAGMENT :
// l'atténuation de la zone de sécurité est une boîte, et une boîte interpolée
// entre les quatre coins d'un quad plein écran devient un dégradé bilinéaire
// qui n'a plus rien d'une boîte.
export const ZONE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform vec2 uResolution;

  float zoneSecurite() {
    vec2 ndc = (gl_FragCoord.xy / uResolution) * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float a = mix(${PLANCHER_PARTAGE.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${PLANCHER.toFixed(2)} + (a - ${PLANCHER_PARTAGE.toFixed(2)})
         * ${((1 - PLANCHER) / (1 - PLANCHER_PARTAGE)).toFixed(6)};
  }
`;

// Un bruit blanc figé par pixel, borné avant le sin : sur la TV, mediump est du
// vrai fp16 et fract(sin(x) * 43758) s'effondre en bandes plates dès que x
// dépasse la seizaine.
export const HASH_CHUNK = `
  float hash21(vec2 p) {
    return fract(sin(mod(dot(p, vec2(12.9898, 78.233)), 6283.1853)) * 43758.5453);
  }
`;

// ---------------------------------------------------------------------------
// Le fond : du noir, habillé de quatre façons
// ---------------------------------------------------------------------------
const fondVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

function fondFragmentShader(opts) {
  return `
  precision mediump float;

  uniform vec3 uLueurA;        // la teinte des lueurs, qui SUIT celle des tubes
  uniform vec3 uLueurB;
  uniform vec2 uPosA;          // le centre des deux emplacements
  uniform vec2 uPosB;
  uniform float uLueurRayon;
  uniform float uLueurPulse;   // 1 au repos, 1.15 sur le temps
  uniform vec3 uMurTeinte;
  uniform float uVague;        // -1 au repos, sinon 0..1 : la position du front
  uniform float uMurPlein;     // 0..1, le drop
  uniform sampler2D uTypo;
  uniform vec2 uTypoTaille;    // largeur/hauteur du bandeau, unités de hauteur
  uniform float uTypoX;
  uniform float uTypoPresent;
  uniform vec3 uTypoTeinte;
  uniform float uTypoCar;      // quelle classe de caractère s'allume, 0..11
  uniform float uTypoVie;      // 0..1, l'extinction du caractère allumé
  uniform float uGainFond;     // grille, vague, lueurs
  uniform float uGainTypo;     // le tube ÉTEINT du bandeau${opts.glows ? `
  uniform vec3 uGlowPos[${opts.glows}];` : ''}

  varying vec2 vUv;

  ${ZONE_CHUNK}
  ${HASH_CHUNK}

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uAspect, ndc.y);

    // LE FOND EST NOIR. Rien n'est posé dessous : tout ce qui suit s'AJOUTE.
    vec3 col = vec3(0.0);

    ${opts.points ? `
    // 1. LA GRILLE DE POINTS - un mur de LED éteint, vu de près. Elle donne une
    // surface au noir sans le remplir : à 3% elle est sous le seuil de
    // perception d'un aplat mais au-dessus de celui d'une texture.
    vec2 gp = fract(gl_FragCoord.xy / ${POINTS_PAS_PX.toFixed(1)}) - 0.5;
    float point = 1.0 - smoothstep(0.10, 0.30, length(gp));
    col += uMurTeinte * point * ${POINTS_NIVEAU.toFixed(4)} * uGainFond;
    ` : ''}

    ${opts.mur ? `
    // 2. LE MUR LED. Éteint par défaut ; une vague le traverse sur le temps
    // fort de mesure, et le drop l'allume entièrement. Les pavés, leur jeu de
    // luminosité et leurs coutures viennent de main-stage.js : sans eux une
    // vague est un dégradé qui glisse, avec eux c'est du matériel.
    vec2 mc = gl_FragCoord.xy / ${MUR_PAS_PX.toFixed(1)};
    vec2 mf = fract(mc);
    vec2 mid = floor(mc);
    float pave = smoothstep(0.0, 0.16, mf.x) * smoothstep(1.0, 0.84, mf.x)
               * smoothstep(0.0, 0.16, mf.y) * smoothstep(1.0, 0.84, mf.y);
    // Aucun vrai mur n'a deux pixels identiques.
    float jeu = 0.84 + 0.16 * hash21(mid);
    float couture = 1.0;
    if (mod(mid.x, ${MUR_COUTURE.toFixed(1)}) < 0.5) couture *= 0.70;
    if (mod(mid.y, ${MUR_COUTURE.toFixed(1)}) < 0.5) couture *= 0.70;

    float niveau = uMurPlein * ${MUR_PLEIN_NIVEAU.toFixed(2)};
    ${opts.vague ? `
    if (uVague >= 0.0) {
      // Le front avance de gauche à droite en un temps. Bord d'attaque net,
      // traîne longue : c'est ce qui donne un SENS à la vague. Un profil
      // symétrique se lit comme une barre qui glisse, pas comme une vague.
      float pavePx = (mid.x + 0.5) * ${MUR_PAS_PX.toFixed(1)};
      float dx = (uVague * uResolution.x - pavePx) / 200.0;
      float onde = dx < 0.0 ? exp(-dx * dx * 36.0) : exp(-dx * 1.7);
      niveau = max(niveau, onde * ${MUR_VAGUE_NIVEAU.toFixed(2)} * uGainFond);
    }
    ` : ''}
    // À PLEINE PUISSANCE LE MUR BLANCHIT. Une teinte saturée ne peut pas porter
    // une luminance élevée - un rouge pur plafonne à 0.21 - donc un mur qui
    // resterait à sa teinte ne bloomerait jamais, quelle que soit sa
    // multiplication. C'est aussi ce que fait un vrai mur de LED poussé à bout :
    // le centre part au blanc. La vague, elle, garde sa teinte entière.
    vec3 murCol = mix(uMurTeinte, vec3(1.0), ${MUR_BLANCHIT.toFixed(2)} * uMurPlein);
    vec3 mur = murCol * niveau * ${MUR_GAIN.toFixed(2)};
    // LE PLAFOND D'ABORD, LE MASQUE ENSUITE, et l'ordre est tout. Plafonné
    // après le masque, un pavé assombri par une couture (0.70) dépassait encore
    // le plafond et y était ramené comme un pavé plein : à pleine puissance le
    // mur devenait une dalle unie, la structure de pavés - la seule chose qui
    // le fasse lire comme du matériel - disparaissait, et le cadre entier
    // montait à 243 sur 255. Plafonné avant, le pavé plein bloome et la couture
    // reste en dessous du seuil : la grille survit.
    float lumMur = dot(mur, ${LUMA});
    if (lumMur > ${CRETE_MUR.toFixed(2)}) mur *= ${CRETE_MUR.toFixed(2)} / lumMur;
    col += mur * pave * jeu * couture;
    ` : ''}

    // 3. LES LUEURS RADIALES. Elles marquent la PLACE des deux emplacements et
    // sont là même quand le pictogramme n'y est pas - c'est ce qui fait que le
    // cadre reste composé sur un temps vide au lieu de devenir un écran noir.
    float ra = length(s - uPosA) / uLueurRayon;
    float rb = length(s - uPosB) / uLueurRayon;
    vec3 lueur = uLueurA * exp(-ra * ra * 2.30) + uLueurB * exp(-rb * rb * 2.30);
    lueur *= ${LUEUR_NIVEAU.toFixed(3)} * uLueurPulse * uGainFond;
    // Plafonnées BIEN en dessous du seuil de bloom, et plafonnées ICI, avant de
    // rejoindre le reste : c'est la seule façon de garantir qu'une lueur seule
    // ne déborde jamais, quoi qu'il y ait d'autre à l'écran.
    float mxl = max(lueur.r, max(lueur.g, lueur.b));
    if (mxl > ${CRETE_LUEUR.toFixed(2)}) lueur *= ${CRETE_LUEUR.toFixed(2)} / mxl;
    col += lueur;

    // 4. LE BANDEAU TYPOGRAPHIQUE, en tube néon ÉTEINT. La texture porte trois
    // canaux : R le halo (un trait large), B le tube (un trait fin), G l'indice
    // du caractère. Éteint, on ne voit qu'un contour sombre ; sur le temps, un
    // caractère sur douze s'amorce brièvement.
    if (uTypoPresent > 0.5) {
      vec2 t = vec2((s.x - uTypoX) / uTypoTaille.x + 0.5, s.y / uTypoTaille.y + 0.5);
      if (t.x > 0.0 && t.x < 1.0 && t.y > 0.0 && t.y < 1.0) {
        vec4 tx = texture2D(uTypo, t);
        float halo = tx.r;
        float tube = tx.b;
        float idx = floor(tx.g * ${TYPO_CARS.toFixed(1)});
        float on = abs(idx - uTypoCar) < 0.5 ? uTypoVie : 0.0;
        vec3 ct = uTypoTeinte * uGainTypo * (tube * ${TYPO_ETEINT_TUBE.toFixed(3)}
                                           + halo * ${TYPO_ETEINT_HALO.toFixed(3)});
        ct += uTypoTeinte * on * (tube * 0.55 + halo * 0.24);
        ct += vec3(1.0) * on * tube * 0.22;
        float mxt = max(ct.r, max(ct.g, ct.b));
        if (mxt > ${CRETE_TYPO.toFixed(2)}) ct *= ${CRETE_TYPO.toFixed(2)} / mxt;
        col += ct;
      }
    }
${opts.glows ? `
    // 5. LA LUEUR DES CHARGES (Pyro seulement). Le feu éclaire le fond autour
    // de lui : chaud, pas palette, c'est sa propre lumière. Accumulée puis
    // repliée par Reinhard, pour qu'un nombre quelconque de charges au pic
    // d'allumage sur la même image ne fasse pas sauter la luminance.
    vec3 glow = vec3(0.0);
    for (int i = 0; i < ${opts.glows}; i++) {
      vec3 g = uGlowPos[i];
      if (g.z <= 0.0) continue;
      float d = length((s - g.xy) * vec2(1.0, 1.35));
      glow += vec3(1.0, 0.55, 0.20) * g.z * 0.32 / (1.0 + d * d * 26.0);
    }
    col += glow / (1.0 + max(glow.r, max(glow.g, glow.b)));
` : ''}
    col *= zoneSecurite();

    // LE DITHER, ET IL EST MULTIPLICATIF. Une lueur radiale très douce sur du
    // noir passe par quelques dizaines de valeurs sur 255 et se découpe en
    // anneaux concentriques parfaitement visibles ; un bruit figé les dissout.
    // Mais un dither ADDITIF posé ici est posé en LUMIÈRE LINÉAIRE, où la
    // courbe de sortie a une pente de 12.9 près de zéro : un bruit d'un pixel
    // et demi ressortait à dix pixels sur 255, sur TOUT le cadre, et le noir
    // n'était plus noir (mesuré : 19% de pixels vraiment noirs dans un coin).
    // Multiplicatif, il vaut une fraction de la marche de sortie là où il y a
    // du signal, et exactement zéro sur du noir - qui reste donc du noir.
    col *= 1.0 + (hash21(gl_FragCoord.xy) - 0.5) * ${DITHER.toFixed(4)};

    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;
}

// ---------------------------------------------------------------------------
// La couleur : UNE, et elle change
// ---------------------------------------------------------------------------
// Une seule teinte à l'écran à tout instant. Elle change sur les MESURES, en
// tirant dans vividColors. Sur une phrase sur quatre, une seconde teinte est
// autorisée - gauche et droite dans deux complémentaires - puis retour à une.
// Jamais trois : le contraste de ce visualiseur vient de la lumière sur le
// noir, pas de l'opposition de deux couleurs.
const REPLI = [0xe8402a, 0x1b2ad8, 0xf2c500];
const SAT = 0.85;
const LUM_TUBE = 0.56;
const LUM_LUEUR = 0.50;

function versLineaire(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }

// THREE.ColorManagement est désactivé dans ce build : setHSL y écrit le triplet
// sRGB BRUT sans le convertir vers le linéaire, et une couleur posée par setHSL
// arrive 3,5 fois trop claire. Sur un visualiseur qui bloome volontairement,
// cette erreur-là ne se voit pas comme une erreur : elle se voit comme un écran
// qui brûle. On convertit à la main.
export function poserHSL(couleur, h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 1) + 1) % 1) * 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) { r = c; g = x; } else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; } else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; } else { r = c; b = x; }
  const m = l - c / 2;
  couleur.setRGB(versLineaire(r + m), versLineaire(g + m), versLineaire(b + m));
  return couleur;
}

function teinteDe(hex) {
  const r = ((hex >> 16) & 255) / 255, g = ((hex >> 8) & 255) / 255, b = (hex & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (d < 1e-6) return null;
  let h;
  if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)); else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
  return h / 6;
}
function ecartTeinte(a, b) { const d = Math.abs(a - b) % 1; return d > 0.5 ? 1 - d : d; }

// Interpolation circulaire d'une teinte : par le plus court chemin sur le
// cercle. Une interpolation linéaire de 0.95 vers 0.05 traverserait tout le
// spectre au lieu de franchir la frontière du rouge.
function versTeinte(de, vers, k) {
  let d = vers - de;
  if (d > 0.5) d -= 1; else if (d < -0.5) d += 1;
  return (((de + d * k) % 1) + 1) % 1;
}

export class PosterBackdrop {
  // options :
  //   points, vague, mur   ce que le tier allume (voir BACKDROP_TIERS)
  //   glows                un tableau de THREE.Vector3 (x, y, intensité) en
  //                        unités de hauteur - les charges de Pyro ; absent
  //                        chez Poster
  //   random               la source d'aléa pour le tirage des teintes ;
  //                        Poster passe son propre générateur, déterministe
  //                        par morceau
  //   renderOrder          0 chez Poster, très négatif chez Pyro
  constructor(options) {
    const o = options || {};
    this._opts = { points: !!o.points, vague: !!o.vague, mur: !!o.mur };
    // DEUX GAINS, PAR VISUALISEUR. Ce décor est partagé par poster, pyro et
    // main-stage : changer les constantes les changerait tous les trois.
    // gainFond porte la grille de points, la vague du mur LED et les lueurs
    // radiales ; gainTypo porte le bandeau typographique ÉTEINT - le caractère
    // qui s'amorce sur le temps garde sa propre clarté, sans quoi le contraste
    // entre le tube mort et l'amorce disparaîtrait, et c'est lui le sujet.
    this._gainFond = o.gainFond != null ? o.gainFond : 1;
    this._gainTypo = o.gainTypo != null ? o.gainTypo : 1;
    this._glows = o.glows || null;
    this._random = o.random || Math.random;
    this._renderOrder = o.renderOrder || 0;
  }

  get uniforms() { return this._uniforms; }
  get mesh() { return this._fond; }
  get teinteA() { return this._teinteA; }
  get teinteB() { return this._teinteB; }
  get bicolore() { return this._bicolore; }

  init(scene) {
    this._scene = scene;
    this._uniforms = {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather,
      uResolution: { value: new THREE.Vector2(1280, 720) },
      // le fond
      uLueurA: { value: new THREE.Color() },
      uLueurB: { value: new THREE.Color() },
      uPosA: { value: new THREE.Vector2(-0.6, 0) },
      uPosB: { value: new THREE.Vector2(0.6, 0) },
      uLueurRayon: { value: 0.6 },
      uLueurPulse: { value: 1 },
      uGainFond: { value: this._gainFond },
      uGainTypo: { value: this._gainTypo },
      uMurTeinte: { value: new THREE.Color() },
      uVague: { value: -1 },
      uMurPlein: { value: 0 },
      uTypo: { value: null },
      uTypoTaille: { value: new THREE.Vector2(4, 1.4) },
      uTypoX: { value: 0 },
      uTypoPresent: { value: 0 },
      uTypoTeinte: { value: new THREE.Color() },
      uTypoCar: { value: 0 },
      uTypoVie: { value: 0 },
      // la teinte courante des tubes - portée ici parce que la couleur est
      // un seul état (voir l'en-tête) ; Poster y branche ses pictogrammes
      uTubeA: { value: new THREE.Color() },
      uTubeB: { value: new THREE.Color() }
    };
    if (this._glows) this._uniforms.uGlowPos = { value: this._glows };

    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this._fondGeo = g;
    this._fondMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: fondVertexShader,
      fragmentShader: fondFragmentShader(Object.assign({ glows: this._glows ? this._glows.length : 0 }, this._opts)),
      depthTest: false, depthWrite: false, transparent: false
    });
    this._fond = new THREE.Mesh(g, this._fondMat);
    this._fond.frustumCulled = false;
    this._fond.renderOrder = this._renderOrder;
    scene.add(this._fond);

    // --- état ---
    this._temps = 0;
    this._tempsIndex = 0;
    this._mesureIndex = 0;
    this._phraseIndex = 0;
    this._vagueT = -1;
    this._pulse = 0;
    this._typoVie = 0;
    this._typoCran = 0;
    this._teintes = [];
    this._teinteA = 0.02;
    this._teinteB = 0.02;
    this._bicolore = false;
    // Les lueurs SUIVENT les tubes en un temps : elles sont tenues en HSL et
    // interpolées, pas recopiées.
    this._lueurA = 0.02;
    this._lueurB = 0.02;
    this._typoTexture = null;
  }

  // --- le bandeau typographique --------------------------------------------
  // Trois canaux dans une seule texture, parce qu'un tube néon a besoin de deux
  // largeurs et que le caractère qui s'allume a besoin de savoir qui il est :
  //   R  un trait LARGE   -> le halo
  //   G  l'indice du caractère modulo 12, en colonnes pleine hauteur
  //   B  un trait FIN     -> le tube
  // Les trois passes se composent en 'lighter' : chacune n'écrit que son canal,
  // donc elles s'additionnent sans jamais s'écraser.
  setTitle(texte) {
    if (this._typoTexture) { this._typoTexture.dispose(); this._typoTexture = null; }
    this._uniforms.uTypoPresent.value = 0;
    if (!texte) return;
    const H = 512;
    const c = document.createElement('canvas');
    let ctx = c.getContext('2d');
    const police = '900 ' + Math.round(H * 0.72) + 'px "Helvetica Neue", Helvetica, Arial, sans-serif';
    ctx.font = police;
    const marge = 48;
    const large = Math.max(64, Math.min(8192, Math.ceil(ctx.measureText(texte).width) + marge * 2));
    c.width = large; c.height = H;
    ctx = c.getContext('2d');
    ctx.font = police;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;

    // G : une colonne pleine hauteur par caractère. Le filtrage linéaire fait
    // baver l'indice sur un pixel de part et d'autre d'une frontière ; c'est
    // sans conséquence, le caractère allumé y perd ou y gagne un liseré d'un
    // pixel et l'oeil ne voit qu'un caractère qui claque.
    ctx.globalCompositeOperation = 'source-over';
    for (let i = 0; i < texte.length; i++) {
      const x0 = marge + ctx.measureText(texte.slice(0, i)).width;
      const x1 = marge + ctx.measureText(texte.slice(0, i + 1)).width;
      // (i%12 + 0.5)/12 : la demi-case met la valeur au MILIEU de son palier,
      // ce qui laisse au floor() du shader une demi-case de marge de chaque
      // côté. Sans elle, une frontière de palier tombe pile sur un indice.
      const v = Math.round(((i % TYPO_CARS) + 0.5) / TYPO_CARS * 255);
      ctx.fillStyle = 'rgb(0,' + v + ',0)';
      ctx.fillRect(x0, 0, Math.max(1, x1 - x0), H);
    }

    ctx.globalCompositeOperation = 'lighter';
    // R : le halo, un trait large.
    ctx.strokeStyle = 'rgb(255,0,0)';
    ctx.lineWidth = 14;
    ctx.strokeText(texte, marge, H * 0.54);
    // B : le tube, un trait fin. Deux pixels de canevas pour 512 de haut, soit
    // environ deux pixels d'écran une fois le bandeau à sa taille : c'est le
    // « contour sombre de 2 px » demandé, et il est obtenu en TRAÇANT le texte,
    // pas en détourant un texte plein. L'alpha d'un glyphe rempli n'est pas un
    // champ de distance : on n'en tire aucun contour propre à grande échelle.
    ctx.strokeStyle = 'rgb(0,0,255)';
    ctx.lineWidth = 3;
    ctx.strokeText(texte, marge, H * 0.54);

    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.NoColorSpace;
    t.wrapS = THREE.ClampToEdgeWrapping;
    t.wrapT = THREE.ClampToEdgeWrapping;
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    t.needsUpdate = true;
    this._typoTexture = t;
    this._uniforms.uTypo.value = t;
    // Le bandeau fait 1.4 unité de haut ; sa largeur suit le rapport du canevas.
    const haut = 1.4;
    this._uniforms.uTypoTaille.value.set(haut * (large / H), haut);
    this._uniforms.uTypoPresent.value = 1;
  }

  // --- les couleurs ---------------------------------------------------------
  setPalette(palette) {
    const vifs = (palette && palette.vividColors && palette.vividColors.length >= 2)
      ? palette.vividColors : REPLI;
    const teintes = [];
    for (const v of vifs) {
      const h = teinteDe(v);
      if (h == null) continue;
      // Deux teintes trop voisines ne sont pas deux teintes : à l'écran le
      // changement de mesure passerait inaperçu.
      if (teintes.every((t) => ecartTeinte(t, h) > 0.06)) teintes.push(h);
    }
    while (teintes.length < 2) teintes.push((teintes.length ? teintes[0] : 0.02) + 0.37);
    this._teintes = teintes;
    this._teinteA = teintes[0];
    this._teinteB = teintes[0];
    this._lueurA = teintes[0];
    this._lueurB = teintes[0];
    this._bicolore = false;
    this._ecrireCouleurs();
  }

  // La complémentaire disponible : la teinte du vivier la plus éloignée de h.
  _opposee(h) {
    let best = h, dist = -1;
    for (const t of this._teintes) {
      const d = ecartTeinte(t, h);
      if (d > dist) { dist = d; best = t; }
    }
    return best;
  }

  _ecrireCouleurs() {
    const u = this._uniforms;
    poserHSL(u.uTubeA.value, this._teinteA, SAT, LUM_TUBE);
    poserHSL(u.uTubeB.value, this._bicolore ? this._teinteB : this._teinteA, SAT, LUM_TUBE);
    poserHSL(u.uLueurA.value, this._lueurA, SAT, LUM_LUEUR);
    poserHSL(u.uLueurB.value, this._bicolore ? this._lueurB : this._lueurA, SAT, LUM_LUEUR);
    // Le mur et le bandeau prennent la teinte COURANTE, jamais la seconde :
    // deux teintes à l'écran, c'est deux, pas trois.
    poserHSL(u.uMurTeinte.value, this._teinteA, SAT, LUM_TUBE);
    poserHSL(u.uTypoTeinte.value, this._teinteA, SAT, LUM_TUBE);
  }

  // OÙ SE POSENT LES DEUX LUEURS (et, chez Poster, les deux pictogrammes).
  // Chacune est centrée dans SON couloir - l'espace libre entre le bord du
  // cadre et le bord de la pochette - et non collée contre la pochette. Les
  // deux couloirs sont calculés séparément : la zone de sécurité est MESURÉE
  // sur la pochette réelle, son centre n'est pas forcément celui de l'écran,
  // et deux couloirs de largeurs différentes ne se centrent pas au même
  // endroit.
  //
  // Le résultat est ensuite borné pour qu'un pictogramme reste entier à
  // l'écran, avec son écho le plus large : au tirage le plus grand possible
  // (+20% de taille, punch à 145%, troisième anneau à 1.6x), un emplacement
  // couvre RAYON_MAX de part et d'autre de son centre. Sur un cadre 16:9 la
  // borne ne mord pas - le milieu du couloir tombe juste dedans - mais sur un
  // cadre étroit elle est la seule chose qui empêche le pictogramme de sortir.
  emplacementsX() {
    // R19 : les couloirs se comptent depuis le bloc de scène (la pochette telle
    // qu'elle serait au centre), pas depuis la pochette où qu'elle soit.
    const sc = safeZoneUniforms.uStageCenter.value;
    const sh = safeZoneUniforms.uStageHalfSize.value;
    const asp = safeZoneUniforms.uAspect.value || 1.78;
    const r = RAYON_MAX;
    // LE MILIEU SE CALCULE SUR LE BORD DE LA POCHETTE, PAS SUR LA MARGE. Compter
    // le couloir depuis la pochette moins MARGE_POCHETTE décale le milieu vers
    // l'extérieur de la moitié de la marge : mesuré, 13.1% d'écart au bord
    // d'écran contre 16.0% à la pochette, là où les deux doivent être égaux. La
    // marge reste, mais comme BORNE - la distance minimale à la pochette sur un
    // cadre trop étroit pour centrer - et non comme bord du couloir.
    const bordG = sc.x - sh.x;
    const bordD = sc.x + sh.x;
    const garde = Math.max(r, MARGE_POCHETTE);
    const centrer = (bordExt, bordInt, signe) => {
      const m = (bordExt + bordInt) * 0.5;
      const lo = signe < 0 ? bordExt + r : bordInt + garde;
      const hi = signe < 0 ? bordInt - garde : bordExt - r;
      // Couloir plus étroit que le pictogramme : le milieu reste le moins
      // mauvais choix, et il déborde symétriquement des deux côtés.
      return lo <= hi ? Math.max(lo, Math.min(hi, m)) : m;
    };
    return [centrer(-asp, bordG, -1), centrer(asp, bordD, 1)];
  }

  // --- les trois horloges ---------------------------------------------------

  // Sur le temps : la pulsation des lueurs, et le caractère du bandeau qui
  // s'allume. Le pas de 5 est premier avec 12 : le caractère allumé fait le
  // tour du bandeau au lieu de descendre en file.
  onBeat() {
    this._pulse = 1;
    this._typoVie = 1;
    this._uniforms.uTypoCar.value = (this._tempsIndex * 5) % TYPO_CARS;
    this._tempsIndex++;
  }

  onBar() {
    this._mesureIndex++;
    // LA TEINTE CHANGE SUR LA MESURE. On tire une autre teinte du vivier de
    // couleurs de la pochette - jamais la même deux fois de suite, sans quoi
    // une pochette à deux teintes proches donnerait une mesure sans événement.
    if (this._teintes.length > 1) {
      let h = this._teinteA, garde = 0;
      while (h === this._teinteA && garde++ < 8) {
        h = this._teintes[Math.floor(this._random() * this._teintes.length)];
      }
      this._teinteA = h;
      if (this._bicolore) this._teinteB = this._opposee(h);
    }
    // LA VAGUE traverse le mur de gauche à droite en un temps.
    if (this._opts.vague) this._vagueT = 0;
    // Le bandeau saute d'un cran d'un caractère sur la mesure.
    this._typoCran++;
  }

  onPhrase() {
    this._phraseIndex++;
    // UNE PHRASE SUR QUATRE est bicolore : gauche et droite dans deux
    // complémentaires. Les trois autres reviennent à une seule teinte.
    const avant = this._bicolore;
    this._bicolore = (this._phraseIndex % PHRASE_BICOLORE) === 0;
    this._teinteB = this._bicolore ? this._opposee(this._teinteA) : this._teinteA;
    // En SORTANT du bicolore, la seconde lueur est recalée d'un coup. La
    // laisser rejoindre la teinte courante par le retard remettrait une
    // troisième teinte à l'écran, pour un temps, juste après l'avoir éliminée.
    // Recalée vers une teinte DÉJÀ présente, elle n'introduit rien.
    if (avant && !this._bicolore) this._lueurB = this._teinteA;
  }

  // --- l'image ----------------------------------------------------------------
  // periode : la durée d'un temps, en secondes (jamais nulle).
  // murPlein : le mur LED allumé - true (ou 1) pour la mesure du drop de
  // Poster, ou une fraction pour un consommateur qui ne veut pas le blanc
  // plein cadre (Pyro l'allume à 0.25 derrière son mur de feu).
  update(deltaTime, periode, murPlein) {
    this._temps += deltaTime;
    const u = this._uniforms;

    // --- la vague : le front traverse le mur en UN temps ---
    if (this._vagueT >= 0) {
      this._vagueT += deltaTime / periode;
      u.uVague.value = this._vagueT >= 1.4 ? -1 : this._vagueT;
      if (this._vagueT >= 1.4) this._vagueT = -1;
    } else {
      u.uVague.value = -1;
    }

    // LE MUR S'ALLUME ENTIÈREMENT PENDANT LA MESURE DU DROP, puis s'éteint d'un
    // coup : « extinction générale et retour ». Pas de fondu de sortie.
    u.uMurPlein.value = this._opts.mur && murPlein ? (murPlein === true ? 1 : Math.min(1, +murPlein)) : 0;

    // --- les couleurs : la lueur SUIT le tube en un temps ---
    // SAUF PENDANT UNE PHRASE BICOLORE, où elle suit instantanément. Les deux
    // permissions du brief - « la lueur suit en 1 temps » et « une seconde
    // teinte est autorisée sur les phrases » - se composent en une TROISIÈME
    // teinte : pendant une phrase bicolore, un changement de teinte à la mesure
    // laisse à l'écran les deux nouvelles teintes des tubes ET les deux
    // anciennes des lueurs. Mesuré sur 400 images : 14 en faute, toutes avec le
    // drapeau bicolore levé ; la même scène sans le retard en compte 1, et
    // celle-là est un artefact de l'histogramme de teintes (un halo dont ACES
    // creuse le milieu), pas une troisième couleur.
    const k = this._bicolore ? 1 : Math.min(1, deltaTime / (LUEUR_SUIVI_TEMPS * periode));
    this._lueurA = versTeinte(this._lueurA, this._teinteA, k);
    this._lueurB = versTeinte(this._lueurB, this._bicolore ? this._teinteB : this._teinteA, k);
    this._ecrireCouleurs();

    // --- les pulsations qui retombent ---
    this._pulse = Math.max(0, this._pulse - deltaTime / (periode * 0.45));
    this._typoVie = Math.max(0, this._typoVie - deltaTime / (periode * 0.35));
    u.uLueurPulse.value = 1 + LUEUR_PULSE * this._pulse;
    u.uTypoVie.value = this._typoVie;

    // --- le bandeau : il GLISSE lentement et SAUTE d'un cran sur la mesure ---
    const largeurCran = u.uTypoTaille.value.x / 24;
    u.uTypoX.value = -((this._temps * TYPO_VITESSE + this._typoCran * largeurCran)
      % (u.uTypoTaille.value.x + 4)) + 2;

    if (typeof window !== 'undefined') {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      u.uResolution.value.set(window.innerWidth * dpr, window.innerHeight * dpr);
    }
    // Le rayon des lueurs vaut 30% de la LARGEUR du cadre ; la largeur en
    // unités de hauteur vaut 2 * aspect.
    u.uLueurRayon.value = LUEUR_RAYON * 2 * (safeZoneUniforms.uAspect.value || 1.78);

    // Les lueurs marquent la place des emplacements, pictogramme ou pas.
    const sc = safeZoneUniforms.uStageCenter.value;   // R19
    const [xG, xD] = this.emplacementsX();
    u.uPosA.value.set(xG, sc.y);
    u.uPosB.value.set(xD, sc.y);
  }

  dispose() {
    if (this._scene) this._scene.remove(this._fond);
    this._fondGeo.dispose();
    this._fondMat.dispose();
    if (this._typoTexture) { this._typoTexture.dispose(); this._typoTexture = null; }
  }
}
